import { FC, MutableRefObject, ReactNode, useRef } from "react";
import ProductPageBreadcrumb from "./Breadcrumb";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import classNames from "classnames";
import ProductStickySidebar from "./Sidebar";
import ProductPageHeaderContent from "./HeaderContent";

import MobileHeader from "../mobile-header";
import Tabs, { HeaderItems } from "@modules/course/non-access/header";
import { useAccess } from "@templates/course/hooks/useAccess";
import { useStickyLayoutOffsets } from "../hook/useStickyLayoutOffsets";

interface Props {
  children: ReactNode;
  headerItems: HeaderItems;
  visibleTarget: string;
  spyOffsetRef: MutableRefObject<number>;
}

const ProductPageLayout: FC<Props> = ({
  children,
  headerItems,
  visibleTarget,
  spyOffsetRef,
}) => {
  const isMobile = useIsMobile();
  const tabsRef = useRef<HTMLElement | null>(null);

  const {
    courseData: {
      product: { title, id: productId },
    },
    shouldRenderSubscriptionTopBanner,
  } = useAccess();

  const {
    headerRef,
    stickyHeader,
    isScrollVariant,
    offsetTop,
    sidebarTop,
    navOffset,
    visibleCompactHeight,
    clickScrollOffset,
  } = useStickyLayoutOffsets({
    isMobile,
    shouldRenderSubscriptionTopBanner,
    spyOffsetRef,
  });

  return (
    <div className="w-full relative ">
      <MobileHeader />
      <ProductPageBreadcrumb />

      <div
        className="sticky z-40 hidden h-0 w-full overflow-visible md:block"
        style={{ top: offsetTop }}
      >
        <div
          ref={stickyHeader}
          aria-hidden={!isScrollVariant}
          className={classNames(
            "w-full py-4 bg-fds-brand-light transition-opacity duration-150 ease-linear",
            isScrollVariant ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        >
          <div className="max-w-[1440px] mx-auto px-4 lg:px-5 xl:px-fds-120">
            <h1 className="headline-lg header-text-max-w lg:w-2/3">{title}</h1>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="bg-fds-brand-light min-h-[272px] py-6 lg:py-10">
          <div className="max-w-[1440px] mx-auto px-4 lg:px-5 xl:px-fds-120">
            <div className="flex w-full lg:justify-between relative">
              <div className="w-full lg:w-2/3">
                <div ref={headerRef}>
                  <ProductPageHeaderContent />
                </div>
              </div>
            </div>
          </div>
        </div>

        <Tabs
          ref={tabsRef}
          siteHeaderOffset={navOffset}
          items={headerItems}
          activeElement={visibleTarget}
          headerHeight={visibleCompactHeight}
          scrollOffset={clickScrollOffset}
        />
        <div
          className={classNames(
            isMobile ? "relative  z-40" : "section-container mt-6",
            "mx-auto "
          )}
        >
          <div className="grid grid-cols-3 w-full lg:justify-between relative">
            <div className="col-span-3 lg:col-span-2">
              <div className="md:pr-0 lg:pl-2">{children}</div>
            </div>
            <div className="hidden lg:block lg:col-span-1" />
          </div>
        </div>

        <div className="hidden lg:block absolute inset-0  pointer-events-none">
          <div className="max-w-[1440px] mx-auto h-full">
            <div className="flex justify-end h-full pt-6 lg:pt-10">
              <div className="w-1/3 pointer-events-auto">
                <ProductStickySidebar key={productId} stickyTop={sidebarTop} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPageLayout;
